/** Narrow D04 role binding; this reads prose, never writes owner/CFS values. */
export const PHASE_DELTA_BINDING_VERSION = 'phase-delta-binding-v1' as const;

export interface PhaseDeltaRole {
  phaseSequence: number;
  metric: 'addition' | 'cumulative';
  powerBasis?: 'it-load' | 'facility';
}

/**
 * Accept only a closed owner-phase label/cell and the explicit parenthetical
 * `(+N MW this phase)`. A plus sign alone, a baseline comparison, utility
 * system quantity, arithmetic expression or another sentence is not this role.
 * Offsets refer to length-preserving citation-stripped text.
 */
export function phaseDeltaRoleAt(text: string, index: number): PhaseDeltaRole | undefined {
  const lineStart = text.lastIndexOf('\n', index - 1) + 1;
  const lineEnd = text.indexOf('\n', index);
  const line = text.slice(lineStart, lineEnd < 0 ? text.length : lineEnd);
  const localIndex = index - lineStart;
  const before = line.slice(0, localIndex);
  const start = Math.max(line.lastIndexOf('|', localIndex - 1), line.lastIndexOf(';', localIndex - 1),
    before.lastIndexOf('. '), before.lastIndexOf('! '), before.lastIndexOf('? ')) + 1;
  const sentenceEnd = /[.!?](?=\s|$)/.exec(line.slice(localIndex));
  const ends = [line.indexOf('|', localIndex), line.indexOf(';', localIndex),
    sentenceEnd ? localIndex + sentenceEnd.index + 1 : -1].filter(end => end >= 0);
  const end = ends.length ? Math.min(...ends) : line.length;
  const cell = line.slice(start, end);
  let cursor = /^\s*(?:[-*]\s*)?(?:(?:Description|Title|Capacity\s*MW):\s*)?/i.exec(cell)![0].length;
  const phase = /^Phase\s*(\d+)\s*:?\s*/i.exec(cell.slice(cursor));
  let phaseSequence: number;
  if (phase) {
    phaseSequence = Number(phase[1]);
    cursor += phase[0].length;
  } else {
    // Only a capacity cell may inherit its own single flattened row identity.
    // No distant paragraph/description phase carry is added here.
    if (!/^\s*(?:[-*]\s*)?Capacity\s*MW:/i.test(cell)) return undefined;
    const rows = [...line.matchAll(/(?:^|\|)\s*(?:-\s*)?Phase:\s*Phase\s*(\d+)\b/gi)];
    if (rows.length !== 1) return undefined;
    phaseSequence = Number(rows[0]![1]);
  }
  if (!Number.isSafeInteger(phaseSequence) || phaseSequence < 1) return undefined;
  const cumulative = /^(\d[\d,]*(?:\.\d+)?)\s*MW\s+cumulative(?:\s+(IT|facility)(?:[- ](?:load|demand))?)?\s*/i.exec(cell.slice(cursor));
  const cumulativeIndex = cursor;
  if (cumulative) cursor += cumulative[0].length;
  const addition = /^\(\s*\+\s*(\d[\d,]*(?:\.\d+)?)\s*MW\s+this\s+phase\s*\)\s*[.!]?\s*$/i.exec(cell.slice(cursor));
  if (!addition) return undefined;
  const heading = [...text.slice(0, lineStart).matchAll(/^#{1,6}\s+(.+)$/gm)].at(-1)?.[1] ?? '';
  if (!cumulative?.[2] && /\b(?:utility|grid|transmission|distribution)\s+(?:system|network|capacity|additions?)\b/i.test(heading)) return undefined;
  const additionIndex = cursor + addition[0].indexOf(addition[1]!);
  const inCell = localIndex - start;
  const metric = inCell === additionIndex ? 'addition'
    : cumulative && inCell === cumulativeIndex ? 'cumulative' : undefined;
  if (!metric) return undefined;
  return { phaseSequence, metric,
    ...(cumulative?.[2] ? { powerBasis: cumulative[2].toLowerCase() === 'it' ? 'it-load' as const : 'facility' as const } : {}) };
}
